import React, { memo } from 'react';

import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { SelectionToolbarPlugin } from '@atlaskit/editor-plugins/selection-toolbar';

import { EditorControlsBanner } from '@confluence/editor-action-bar/entry-points/EditorControlsBanner';
import { SmartCreateFeedbackBannerLoader } from '@confluence/editor-action-bar/entry-points/SmartCreateFeedbackBannerLoader';
import { useIsControlsBannerDismissed } from '@confluence/editor-action-bar/entry-points/useIsControlsBannerDismissed';
import { useOnboardingBannerLogic } from '@confluence/onboarding-editor';
import type { TriggerAnalyticsEventType } from '@confluence/editor-features/entry-points/TriggerAnalyticsEventType';
import { fg } from '@confluence/feature-gating';

export type Props = {
	isLivePage: boolean;
	isEmbeddedConfluence: boolean;
	viewMode?: boolean;
	spaceKey: string;
	editorFullPagePresetAPI: PublicPluginAPI<[SelectionToolbarPlugin]> | undefined;
	triggerAnalyticsEvent?: TriggerAnalyticsEventType;
	contentId: string;
	version: number;
	isShowingOfflineBanner: boolean;
};

export const FullPageEditorBannerWrapper = memo(
	({
		isLivePage,
		isEmbeddedConfluence,
		viewMode,
		spaceKey,
		triggerAnalyticsEvent,
		contentId,
		version,
		isShowingOfflineBanner,
	}: Props) => {
		const { isControlsBannerDismissed, onDismiss, bannerType } =
			useIsControlsBannerDismissed(isLivePage);

		const { isVisible: isOnboardingEditorDraftBannerVisible } = useOnboardingBannerLogic({
			contentId,
			version,
			isShowingOfflineBanner,
			spaceKey,
			setIsOnboardingBannerDataLoaded: () => {},
		});
		const shouldShowControlsBanner =
			!viewMode &&
			!isControlsBannerDismissed &&
			spaceKey !== 'XFLOW' &&
			!isOnboardingEditorDraftBannerVisible;

		if (shouldShowControlsBanner) {
			if (fg('cc_activation_hide_changeboarding_for_new_users')) {
				return !fg('cc_activation_confluence_new_user') ? (
					<EditorControlsBanner
						onDismiss={onDismiss}
						triggerAnalyticsEvent={triggerAnalyticsEvent}
						isLivePage={isLivePage}
						isEmbeddedConfluence={isEmbeddedConfluence}
						bannerType={bannerType}
					/>
				) : null;
			}
			return (
				<EditorControlsBanner
					onDismiss={onDismiss}
					triggerAnalyticsEvent={triggerAnalyticsEvent}
					isLivePage={isLivePage}
					isEmbeddedConfluence={isEmbeddedConfluence}
					bannerType={bannerType}
				/>
			);
		}

		if (spaceKey !== 'XFLOW' && fg('aifc_create_enabled')) {
			return <SmartCreateFeedbackBannerLoader />;
		}
	},
);
